import {
  Box,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@chakra-ui/react';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import type { PlatformSessionGoalFragment } from '@web-app/generated/platform/graphql';
import { singleDigitDateFormat } from '@web-app/utils/date-time.utils';
import { dateComparator } from '@web-app/utils/sortComparators.utils';
import { format } from 'date-fns';
import { useMemo, useState } from 'react';

import type { ArchivedGoalsTableProps } from './types';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    return format(new Date(dateString), singleDigitDateFormat);
  } catch {
    return '-';
  }
};

export const ArchivedGoalsTable = ({
  goals,
  isLoading = false,
}: ArchivedGoalsTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'archivedAt', desc: true },
  ]);

  const columns = useMemo<ColumnDef<PlatformSessionGoalFragment>[]>(
    () => [
      {
        id: 'content',
        accessorFn: (row) => row.content,
        header: 'Goal',
        cell: ({ row }) => (
          <Text color="text.default" fontSize="sm">
            {row.original.content}
          </Text>
        ),
        enableSorting: false,
      },
      {
        id: 'createdAt',
        accessorFn: (row) => row.createdAt,
        header: 'Created',
        cell: ({ row }) => (
          <Text color="text.muted" fontSize="sm">
            {formatDate(row.original.createdAt)}
          </Text>
        ),
        sortingFn: (rowA, rowB) =>
          dateComparator('ASC')(
            rowA.original.createdAt,
            rowB.original.createdAt,
          ),
      },
      {
        id: 'archivedAt',
        accessorFn: (row) => row.archivedAt,
        header: 'Archived',
        cell: ({ row }) => (
          <Text color="text.muted" fontSize="sm">
            {formatDate(row.original.archivedAt)}
          </Text>
        ),
        sortingFn: (rowA, rowB) =>
          dateComparator('ASC')(
            rowA.original.archivedAt,
            rowB.original.archivedAt,
          ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: goals,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <Box data-testid="archived-goals-table-loading">
        <VStack spacing={2} align="stretch">
          <Skeleton height="32px" />
          <Skeleton height="32px" />
          <Skeleton height="32px" />
        </VStack>
      </Box>
    );
  }

  if (goals.length === 0) {
    return (
      <Box data-testid="archived-goals-table-empty" py={4}>
        <Text color="text.muted" fontSize="sm" textAlign="center">
          This patient has no archived goals
        </Text>
      </Box>
    );
  }

  return (
    <Box data-testid="archived-goals-table" overflowX="auto" borderRadius="sm">
      <Table variant="simple" size="sm">
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  color="text.muted"
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="none"
                  cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                  onClick={header.column.getToggleSortingHandler()}
                  _hover={header.column.getCanSort() ? { opacity: 0.8 } : {}}
                  py={2}
                  px={2}
                  borderColor="border.default"
                >
                  <Box display="flex" alignItems="center">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <Text as="span" color="text.muted" ml={1}>
                        {header.column.getIsSorted()
                          ? header.column.getIsSorted() === 'desc'
                            ? '↓'
                            : '↑'
                          : '↕'}
                      </Text>
                    )}
                  </Box>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              data-testid={`archived-goal-row-${row.original.id}`}
              _last={{ td: { borderBottom: 'none' } }}
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id} py={2} px={2} borderColor="border.default">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
};
